"use client";
import { useState, useEffect, useRef, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import SMSBoxImages from "@/assets/ChatBot.svg";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import TextInput from "@/components/common/TextInput";
import PhoneInput from "@/components/common/PhoneInput";
import CommonButton from "@/components/Button/CommonButton";
import ButtonRightArrow from "@/assets/iconComponents/ButtonRightArrow";
import MaskedTextInput from "../common/MaskedTextInput";
import TextareaInput from "../common/TextareaInput";
import { formSchema } from "@/constants";
import { useTranslations } from "next-intl";
import { useCreateContact } from "@/queries/contact";
import { toast } from "sonner";
import useContactBoxStore from "@/store/useContactBoxStore";
import { localStore } from "@/utils/storage";
import useMeasure from "react-use-measure";
import { CookieConsentContext } from "@/providers/CookieConsentProvider";

export default function ChatBot() {
  const t = useTranslations();
  const { bannerClosed } = useContext(CookieConsentContext);
  const [, setWindowWidth] = useState(0);
  const [open, setOpen] = useState(false);
  const [textRef, textBounds] = useMeasure();
  const isDialogBoxOpen = useContactBoxStore((state) => state.isDialogBoxOpen);
  const updateContactDialogBoxOpen = useContactBoxStore(
    (state) => state.updateContactDialogBoxOpen
  );

  // const updateContactDialogBoxOpen = useContactBoxStore();
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef(null);
  const { mutate: createContactMutate, isPending } = useCreateContact();

  const handleBubbleClick = () => {
    setOpen(true);
    setHovered(false);
  };

  useEffect(() => {
    // setOpen(true);
    // If cookie banner is closed → show chatbot once
    // const isChatbotSeen = localStore.get("chatbotSeen");
    // if (isChatbotSeen === "true") {
    //   setOpen(false);
    // } else {
    //   setOpen(true);
    //   localStore.set("chatbotSeenFirstTime", "true");
    //   const timer = setTimeout(() => {
    //     setOpen(false);
    //     localStore.set("chatbotSeenFirstTime", "true");
    //   }, 20000);

    //   return () => clearTimeout(timer);
    // }
    if (bannerClosed) {
      const isChatbotSeen = localStore.get("chatbotSeen");
      // const isChatbotSeenFirstTime = localStore.get("chatbotSeenFirstTime");

      // If already seen → never open again
      if (isChatbotSeen === "true") {
        setOpen(false);
        return;
      }
      setOpen(true);
      localStore.set("chatbotSeen", "true");

      const timer = setTimeout(() => {
        setOpen(false);
        localStore.set("chatbotSeen", "true");
      }, 20000);

      return () => clearTimeout(timer);
    }
  }, [bannerClosed]);

  // Detect outside click
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        localStore.set("chatbotSeen", "true");
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const baseWidth = 48;
  const expandedWidth = textBounds.width + 60;
  const finalWidth = hovered ? expandedWidth : baseWidth;
  const {
    control: contactControl,
    register: contactRegister,
    handleSubmit: contactHandleSubmit,
    formState: { errors: contactError },
    reset: contactFormReset
  } = useForm({
    resolver: zodResolver(formSchema?.createContactFormSchema(t)),
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      mobilephone: "+46",
      business_id: "",
      content: ""
    }
  });

  const handleApplyContact = (contactData) => {
    createContactMutate(
      { ...contactData },
      {
        onSuccess: () => {
          contactFormReset();
          toast.success("Formuläret skickades in framgångsrikt!");
          updateContactDialogBoxOpen(false);
          window.scrollTo({ top: 0, behavior: "smooth" });
        },
        onError: () => {
          toast.error("Det gick inte att skicka formuläret. Försök igen.");
        }
      }
    );
  };

  useEffect(() => {}, [bannerClosed]);

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
      <AnimatePresence mode="wait">
        {!open ? (
          <motion.div
            key="bubble"
            onClick={handleBubbleClick}
            onHoverStart={() => setHovered(true)}
            onHoverEnd={() => setHovered(false)}
            animate={{
              width: finalWidth,
              opacity: 1,
              scale: 1
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="flex items-center gap-2 bg-[#5C2483] text-white cursor-pointer rounded-full shadow-lg overflow-hidden h-12 px-3"
          >
            <span
              ref={textRef}
              className={`whitespace-nowrap text-24px-normal font-normal ${
                hovered ? "block" : "absolute opacity-0 pointer-events-none"
              }`}
            >
              Kontakta fabriken
            </span>
            <Image
              src={SMSBoxImages}
              alt="Chatbot"
              className="w-6 md:w-7.5 aspect-[40/43] text-center"
            />
          </motion.div>
        ) : (
          <motion.div
            key="card"
            ref={cardRef}
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="
              bg-[#5C2483] text-white rounded-2xl shadow-lg 
              p-4 w-[59.53vw] sm:w-72 md:w-57 2xl:w-80 
              cursor-pointer flex flex-col gap-4 2xl:gap-5
            "
            onClick={() => updateContactDialogBoxOpen(true)}
          >
            <div className="flex items-center justify-between">
              <span className="text-24px-normal font-normal">Kontakta fabriken</span>
              <Image
                src={SMSBoxImages}
                alt="Chatbot"
                className="w-8 md:w-7.5 2xl:w-10 aspect-[40/43]"
              />
            </div>

            <div className="flex flex-col text-18px">
              <p>
                <span>Telefon:</span> +46 46 440 41 00
              </p>
              <p>
                <span>Email:</span> info@finansfabriken.se
              </p>
            </div>

            <p className="text-18px">
              <span>Öppettider:</span>
              <br />
              mån–fre 08:00–17:00
            </p>
            <p className="underline">Kontaktformulär</p>
          </motion.div>
        )}
      </AnimatePresence>
      <Dialog
        open={isDialogBoxOpen}
        onOpenChange={updateContactDialogBoxOpen}
        className="p-4 z-150"
      >
        <DialogContent className="w-[87%] text-ff-beige lg:w-[35.31%] h-[90vh] lg:h-auto overflow-y-auto max-w-none lg:min-w-[508px]  lg:px-[2.34%] lg:py-[2.08%] bg-ff-purple rounded-[20px]">
          <VisuallyHidden>
            <DialogTitle>Profile Dialog</DialogTitle>
          </VisuallyHidden>
          {/* <div className="hubspot-override">
            <HubspotContactForm />
          </div> */}
          <div className="flex flex-col gap-7.5 lg:gap-[2.5vw]">
            <div className="flex flex-col gap-3 text-ff-beige">
              <p className="text-[7.20vw] md:text-[2.91vw] leading-[118%]">Prata med oss!</p>
              <p className="text-22px">
                Vi letar alltid efter nya kontakter och vänner som vill arbeta med oss och fortsätta
                bygga Finansfabriken. Tillsammans stärker vi företagen. Fyll i formuläret eller kom
                förbi på en kaffe!
              </p>
            </div>
            <div className="flex flex-col gap-[2vw] w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                <TextInput
                  id="firstname"
                  type="text"
                  label="Förnamn*"
                  placeholder=""
                  labelClass="text-ff-beige"
                  inputClass="bg-ff-beige-20 w-[50%] !py-0 md:!py-1"
                  register={contactRegister("firstname")}
                  error={contactError.firstname}
                />
                <TextInput
                  id="lastname"
                  type="text"
                  label="Efternamn*"
                  placeholder=""
                  labelClass="text-ff-beige"
                  inputClass="bg-ff-beige-20 w-[50%] !py-0 md:!py-1"
                  register={contactRegister("lastname")}
                  error={contactError.lastname}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                <TextInput
                  id="epost"
                  type="email"
                  label="E-post*"
                  labelClass="text-ff-beige"
                  inputClass={`w-[50%] bg-ff-beige-20 !py-0 md:!py-1`}
                  register={contactRegister("email")}
                  error={contactError.email}
                />
                <PhoneInput
                  id="Telefon:"
                  label="Telefonnummer*"
                  className="w-full"
                  labelClass="text-ff-beige"
                  inputClass={`w-full bg-ff-beige-20 !py-0 md:!py-1`}
                  register={contactRegister("mobilephone")}
                  error={contactError.mobilephone}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                <MaskedTextInput
                  name="business_id"
                  label="Organisationsnummer"
                  id="business_id"
                  mask="dddddd-dddd"
                  replacement={{ d: /\d/ }}
                  labelClass="text-ff-beige"
                  inputClass={`w-[100%] bg-ff-beige-20 !py-0 md:!py-1`}
                  control={contactControl}
                  error={contactError.business_id}
                />
                <TextareaInput
                  id="content"
                  label="Hur kan vi hjälpa till?"
                  textareaClass={`w-[100%] bg-ff-beige-20`}
                  labelClass="text-ff-beige"
                  register={contactRegister("content")}
                  error={contactError.content}
                  isOpen={true}
                />
              </div>
              <div className="flex justify-end mt-4 lg:mt-0">
                <CommonButton
                  text="Skicka"
                  bgColor="#EDD2B9"
                  textColor="#5C2483"
                  className="large-button"
                  svgImages={<ButtonRightArrow size={20} color="#5C2483" />}
                  onClick={contactHandleSubmit(handleApplyContact)}
                  isLoading={isPending}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
